import {Injectable} from '@angular/core';
import {Headers, Http, Response} from '@angular/http';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';

import {User} from '../../models/user';

import * as Config from '../../app/app.config';
import {RegistrationContact} from '../../models/registrationcontact';
import {RegistrationAddress} from '../../models/registrationaddress';
import {RegistrationPersonal} from '../../models/registrationpersonal';
import {RegistrationParent} from '../../models/registrationparent';
import {RegistrationDocument} from '../../models/registrationdocument';
import {RegistrationAffiliated} from '../../models/registrationaffiliated';

/*
  Generated class for the AuthProvider provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular DI.
*/
@Injectable()
export class AuthProvider {

  private data: any;

  constructor(private http: Http) {
  }

  login(credentials: any) {
    if (credentials.email === null || credentials.password === null && credentials.cpf === null) {
      return Observable.throw('Please insert credentials');
    } else {
      let headers = this.createApplicationHeaders();
      headers.append('Authorization', 'Basic ' + btoa(credentials.email + ':' + credentials.password));

      return this.http.post(Config.baseUrl + '/users/authenticate', {'cpf': credentials.cpf}, {headers: headers})
        .map((res: Response) => {
          this.data = res.json().data;

          if (this.data && this.data.user && (this.data.user.affiliatedAccepted === "1" || this.data.user.affiliatedAccepted === "0")) {
            this.data.user.affiliatedAccepted = this.data.user.affiliatedAccepted === "1";
          }

          return this.data;
        });
    }
  }

  accessTokenLogin(accessToken: string) {
    if ((accessToken === null) || (accessToken === undefined)) {
      return Observable.throw('Please insert access token');
    } else {
      let headers = this.createApplicationHeaders();

      let that = this;
      return this.http.get(Config.baseUrl + '/users/accesstoken/' + accessToken, {headers: headers})
        .map((res: Response) => {
          that.data = res.json().data;
          return that.data;
        });
    }
  }

  logout() {
    return Observable.create((observer: any) => {
      this.data = null;
      observer.next(true);
      observer.complete();
    });
  }

  recoverPassword(credentials: any) {
    if (credentials.email === null) {
      return Observable.throw('Please insert credentials');
    } else {
      return this.http.post(Config.baseUrl + '/users/resetpassword', {
        'email': credentials.email
      }).map((res: Response) => res.json());
    }
  }

  updatePassword(data: any) {
    if ((data.password === null) || (data.newPassword === null) || (data.newPasswordCheck === null)) {
      return Observable.throw('Please insert credentials');
    } else {
      let headers = this.createApplicationHeadersWithBearer();

      return this.http.put(Config.baseUrl + '/users/me/password', {
        'password': data.password,
        'newPassword': data.newPassword,
        'newPasswordCheck': data.newPasswordCheck,
      }, {headers: headers})
        .map((res: Response) => res.json());
    }
  }

  private update(endpoint: string, data: any): Observable<Response> {
    return this.http.put(Config.baseUrl + endpoint, data, {
      headers: this.createApplicationHeadersWithBearer()
    }).map((res: Response) => res.json());
  }

  updateContact(data: RegistrationContact) {
    return this.update('/users/me/contact', data);
  }

  updateAddress(data: RegistrationAddress) {
    return this.update('/users/me/address', data);
  }

  updateAddressInvoice(data: RegistrationAddress) {
    return this.update('/users/me/addressinvoice', data);
  }

  updatePersonal(data: RegistrationPersonal) {
    return this.update('/users/me/personal', data);
  }

  updateParent(data: RegistrationParent) {
    return this.update('/users/me/parent', data);
  }

  updateDocument(data: RegistrationDocument) {
    return this.update('/users/me/document', data);
  }

  updateAffiliated(data: RegistrationAffiliated) {
    return this.update('/users/me/affiliated', data);
  }

  optForNotAffiliate() {
    const headers: Headers = this.createApplicationHeadersWithBearer();
    const options: any = {headers: headers};

    return this.http.get(Config.baseUrl + '/users/me/opt-for-not-affiliate', options)
      .map((res: Response) => res.json());
  }

  getUser(): User {
    return this.data.user;
  }

  /**
   * It must be called only after getUser() and change any
   * property (after and on positive save action).
   *
   * @param user User to be setted.
   */
  setUser(user: User) {
    this.data.user = user;
  }

  getToken() {
    return this.data.token;
  }

  createApplicationHeaders() {
    let headers = new Headers();
    headers.append('Application', JSON.stringify({
      'version': Config.version,
      'applicationId': Config.applicationId,
    }));
    headers.append('Access-Control-Allow-Origin', '*');

    return headers;
  }

  createApplicationHeadersWithBearer() {
    let headers = this.createApplicationHeaders();
    headers.append('Authorization', 'Bearer ' + this.data.token);

    return headers;
  }

  getProof(force: boolean): Observable<any> {
    console.log('AuthProvider.getProof', force);
    let that = this;

    return Observable.create((observer: any) => {
      if (force) {
        let headers = that.createApplicationHeadersWithBearer();

        that.http.get(Config.baseUrl + '/users/proof', {headers: headers})
          .map((res: Response) => {
            let result = null;
            try {
              result = res.json();
            } catch (e) {
              console.log(e);
            }

            return result;
          })
          .subscribe((data) => {
            that.data.proof = data;

            observer.next(data);
            observer.complete();
          }, (error: any) => {
            console.log(error);
            observer.error(new Error(error.status));
          });
      } else {
        observer.next(that.data.proof);
        observer.complete();
      }
    });
  }

  hasProofInOtherAccessKind() {
    return ((this.data.hasProofInOtherAccessKind) ? true : false);
  }

  hasProofLocked() {
    return ((this.data.hasProofLocked) ? true : false);
  }

  hasSubscription() {
    return ((this.data.hasSubscription) ? true : false);
  }

  public getProofRequiresAssistance(): Observable<any> {
    return Observable.create((observer: any) => {
      this.http.get(Config.baseUrl + '/users/proof-requires-assistance', {
        headers: this.createApplicationHeadersWithBearer()
      })
        .map((res: Response) => {
          let result: boolean = false;

          try {
            result = res.json().result;
          } catch (e) {
            console.log(e);
          }

          return result;
        })
        .subscribe((data) => {
          observer.next(data);
          observer.complete();
        }, (error: any) => {
          console.log(error);
          observer.error(new Error(error.status));
        });
    });
  }

}



// WEBPACK FOOTER //
// ./src/providers/auth/auth.ts